import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Edit3, 
  Trash2, 
  GripVertical,
  CheckCircle,
  Circle,
  Square,
  Type,
  List,
  ArrowUpDown,
  Image,
  Video,
  Music,
  Save,
  X,
  AlertCircle,
  Database,
  GitBranch
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Badge } from '../../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../ui/tabs';
import { ScrollArea } from '../../ui/scroll-area';
import { Switch } from '../../ui/switch';
import { useToast } from '../../../hooks/useToast';
import { useTenantContext } from '../../../context/TenantContext';
import { 
  addQuestionToQuiz,
  updateQuestion,
  deleteQuestion,
  reorderQuestions,
  addAnswerToQuestion,
  updateAnswer,
  deleteAnswer,
  type Quiz,
  type QuizQuestion,
  type QuizAnswer
} from '../../../services/elearningQuizService';
import { QuestionImportExport } from './QuestionImportExport';
import { QuestionPreview } from './QuestionPreview';
import { QuestionBank } from './QuestionBank';
import { MediaUpload } from './MediaUpload';
import { CustomScoring } from './CustomScoring';
import { ConditionalQuestions } from './ConditionalQuestions';
import { QuestionTags } from './QuestionTags';
import { AnswerFeedback } from './AnswerFeedback';

interface QuestionManagerProps {
  quiz: Quiz;
  onQuizUpdate: (updatedQuiz: Quiz) => void;
}

const QuestionManager: React.FC<QuestionManagerProps> = ({ quiz, onQuizUpdate }) => {
  const { tenant } = useTenantContext();
  const { toast } = useToast();

  const [questions, setQuestions] = useState<QuizQuestion[]>(quiz.questions || []);
  const [isQuestionDialogOpen, setIsQuestionDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<QuizQuestion | null>(null);
  const [isQuestionBankOpen, setIsQuestionBankOpen] = useState(false);
  const [draggedQuestion, setDraggedQuestion] = useState<string | null>(null);

  const [questionForm, setQuestionForm] = useState({
    question_text: '',
    question_type: 'single_choice' as 'multiple_choice' | 'single_choice' | 'true_false' | 'text_input' | 'matching' | 'ordering',
    points: 1,
    explanation: '',
    media_url: '',
    media_type: undefined as 'image' | 'video' | 'audio' | undefined,
    is_required: false,
    answers: [] as { answer_text: string; is_correct: boolean }[]
  });

  useEffect(() => {
    setQuestions(quiz.questions || []);
  }, [quiz.questions]);

  const questionTypes = [
    { value: 'single_choice', label: 'Choix unique', icon: Circle },
    { value: 'multiple_choice', label: 'Choix multiples', icon: CheckCircle },
    { value: 'true_false', label: 'Vrai/Faux', icon: Square },
    { value: 'text_input', label: 'Texte libre', icon: Type },
    { value: 'ordering', label: 'Classement', icon: ArrowUpDown }
  ];

  const getQuestionTypeIcon = (type: string) => {
    const questionType = questionTypes.find(t => t.value === type);
    return questionType ? questionType.icon : Circle;
  };

  const openQuestionDialog = (question?: QuizQuestion) => {
    if (question) {
      setEditingQuestion(question);
      setQuestionForm({
        question_text: question.question_text,
        question_type: question.question_type,
        points: question.points,
        explanation: question.explanation || '',
        media_url: question.media_url || '',
        media_type: question.media_type || null,
        is_required: question.is_required,
        answers: question.answers || []
      });
    } else {
      setEditingQuestion(null);
      setQuestionForm({
        question_text: '',
        question_type: 'single_choice',
        points: 1,
        explanation: '',
        media_url: '',
        media_type: null,
        is_required: true,
        answers: []
      });
    }
    setIsQuestionDialogOpen(true);
  };

  const handleSaveQuestion = async () => {
    if (!tenant?.id) return;

    try {
      let savedQuestion: QuizQuestion;
      
      if (editingQuestion) {
        savedQuestion = await updateQuestion(editingQuestion.id, questionForm, tenant.id);
      } else {
        savedQuestion = await addQuestionToQuiz(quiz.id, questionForm, tenant.id);
      }

      // Mettre à jour la liste des questions
      const updatedQuestions = editingQuestion
        ? questions.map(q => q.id === editingQuestion.id ? savedQuestion : q)
        : [...questions, savedQuestion];

      setQuestions(updatedQuestions);
      onQuizUpdate({ ...quiz, questions: updatedQuestions });
      
      setIsQuestionDialogOpen(false);
      toast({
        title: editingQuestion ? 'Question mise à jour' : 'Question créée',
        description: 'La question a été sauvegardée avec succès',
      });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de sauvegarder la question',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    if (!tenant?.id) return;

    const confirmed = window.confirm('Êtes-vous sûr de vouloir supprimer cette question ?');
    if (!confirmed) return;

    try {
      await deleteQuestion(questionId, tenant.id);
      const updatedQuestions = questions.filter(q => q.id !== questionId);
      setQuestions(updatedQuestions);
      onQuizUpdate({ ...quiz, questions: updatedQuestions });
      
      toast({
        title: 'Question supprimée',
        description: 'La question a été supprimée avec succès',
      });
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer la question',
        variant: 'destructive'
      });
    }
  };

  const addAnswer = () => {
    setQuestionForm(prev => ({
      ...prev,
      answers: [...prev.answers, { answer_text: '', is_correct: false, explanation: '' }]
    }));
  };

  const updateAnswerInForm = (index: number, field: string, value: any) => {
    setQuestionForm(prev => ({
      ...prev,
      answers: prev.answers.map((answer, i) => 
        i === index ? { ...answer, [field]: value } : answer
      )
    }));
  };

  const removeAnswer = (index: number) => {
    setQuestionForm(prev => ({
      ...prev,
      answers: prev.answers.filter((_, i) => i !== index)
    }));
  };

  const handleQuestionsImported = (importedQuestions: QuizQuestion[]) => {
    const updatedQuestions = [...questions, ...importedQuestions];
    setQuestions(updatedQuestions);
    onQuizUpdate({ ...quiz, questions: updatedQuestions });
  };

  const handleQuestionFromBank = async (bankQuestion: any) => {
    if (!tenant?.id) return;

    try {
      // Créer la question dans le quiz à partir de la banque
      const newQuestion = await addQuestionToQuiz(quiz.id, {
        question_text: bankQuestion.question_text,
        question_type: bankQuestion.question_type,
        points: bankQuestion.points,
        explanation: bankQuestion.explanation,
        is_required: bankQuestion.is_required
      }, tenant.id);

      // Ajouter les réponses
      const answers = [];
      for (const answerData of bankQuestion.answers) {
        const newAnswer = await addAnswerToQuestion(newQuestion.id, {
          answer_text: answerData.answer_text,
          is_correct: answerData.is_correct,
          explanation: answerData.explanation
        }, tenant.id);
        answers.push(newAnswer);
      }

      const questionWithAnswers = { ...newQuestion, answers };
      const updatedQuestions = [...questions, questionWithAnswers];
      setQuestions(updatedQuestions);
      onQuizUpdate({ ...quiz, questions: updatedQuestions });

      toast({
        title: 'Question ajoutée',
        description: 'La question a été ajoutée depuis la banque avec succès.'
      });
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la question depuis la banque:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible d\'ajouter la question depuis la banque',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between p-6 border-b border-gray-700">
        <div>
          <h3 className="text-xl font-bold text-white">Gestion des Questions</h3>
          <p className="text-gray-400">
            {questions.length} question{questions.length !== 1 ? 's' : ''} • 
            {questions.reduce((sum, q) => sum + q.points, 0)} points au total
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsQuestionBankOpen(true)}
            className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
          >
            <Database size={16} className="mr-2" />
            Banque
          </Button>
          <QuestionImportExport 
            quiz={quiz} 
            onQuestionsImported={handleQuestionsImported}
          />
          <Button
            onClick={() => openQuestionDialog()}
            className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white hover:text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle Question
          </Button>
        </div>
      </div>

      <ScrollArea className="flex-1 p-6">
        <div className="space-y-4">
          <AnimatePresence>
            {questions.map((question, index) => {
              const IconComponent = getQuestionTypeIcon(question.question_type);
              return (
                <motion.div
                  key={question.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <Card className="bg-gray-800/50 border-gray-700 hover:border-gray-600 transition-colors">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          <div className="flex items-center space-x-2">
                            <GripVertical className="w-4 h-4 text-gray-500 cursor-move" />
                            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                              <span className="text-white text-sm font-bold">{index + 1}</span>
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <IconComponent className="w-4 h-4 text-blue-400" />
                              <Badge variant="outline" className="border-blue-500 text-blue-400">
                                {questionTypes.find(t => t.value === question.question_type)?.label}
                              </Badge>
                              <Badge variant="outline" className="border-green-500 text-green-400">
                                {question.points} point{question.points !== 1 ? 's' : ''}
                              </Badge>
                            </div>
                            <h4 className="text-white font-medium leading-relaxed">
                              {question.question_text}
                            </h4>
                            {question.explanation && (
                              <p className="text-gray-400 text-sm mt-1">
                                💡 {question.explanation}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openQuestionDialog(question)}
                            className="text-gray-400 hover:text-white"
                          >
                            <Edit3 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteQuestion(question.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    {question.answers && question.answers.length > 0 && (
                      <CardContent className="pt-0">
                        <div className="space-y-2">
                          {question.answers.map((answer, answerIndex) => (
                            <div
                              key={answer.id}
                              className={`flex items-center space-x-3 p-2 rounded-lg ${
                                answer.is_correct 
                                  ? 'bg-green-500/10 border border-green-500/20' 
                                  : 'bg-gray-700/30'
                              }`}
                            >
                              <div className="w-6 h-6 flex items-center justify-center">
                                {answer.is_correct ? (
                                  <CheckCircle className="w-4 h-4 text-green-400" />
                                ) : (
                                  <Circle className="w-4 h-4 text-gray-500" />
                                )}
                              </div>
                              <span className={`text-sm ${answer.is_correct ? 'text-green-300' : 'text-gray-300'}`}>
                                {answer.answer_text}
                              </span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                </motion.div>
              );
            })}
          </AnimatePresence>

          {questions.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <List className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">Aucune question</h3>
              <p className="text-gray-400 mb-4">Commencez par créer votre première question</p>
              <Button
                onClick={() => openQuestionDialog()}
                className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Créer une question
              </Button>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Dialog de création/édition de question */}
      <Dialog open={isQuestionDialogOpen} onOpenChange={setIsQuestionDialogOpen}>
        <DialogContent className="max-w-7xl max-h-[95vh] bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white">
              {editingQuestion ? 'Modifier la Question' : 'Nouvelle Question'}
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="questions">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="questions" className="text-white">
                <List className="w-4 h-4 mr-2" />
                Questions ({questions.length})
              </TabsTrigger>
              <TabsTrigger value="bank" className="text-white">
                <Database className="w-4 h-4 mr-2" />
                Banque
              </TabsTrigger>
              <TabsTrigger value="logic" className="text-white">
                <GitBranch className="w-4 h-4 mr-2" />
                Logique
              </TabsTrigger>
            </TabsList>

            <TabsContent value="questions">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-h-[75vh] overflow-hidden">
            {/* Formulaire de création/édition */}
            <ScrollArea className="pr-4">
            <div className="space-y-6">
              {/* Type de question */}
              <div>
                <Label className="text-gray-300">Type de question</Label>
                <Select
                  value={questionForm.question_type}
                  onValueChange={(value: any) => setQuestionForm(prev => ({ ...prev, question_type: value }))}
                >
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    {questionTypes.map(type => {
                      const IconComponent = type.icon;
                      return (
                        <SelectItem key={type.value} value={type.value} className="text-white">
                          <div className="flex items-center">
                            <IconComponent className="w-4 h-4 mr-2" />
                            {type.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Question */}
              <div>
                <Label className="text-gray-300">Question *</Label>
                <Textarea
                  value={questionForm.question_text}
                  onChange={(e) => setQuestionForm(prev => ({ ...prev, question_text: e.target.value }))}
                  placeholder="Saisissez votre question..."
                  className="bg-gray-700 border-gray-600 text-white placeholder-gray-400 resize-none"
                  rows={3}
                />
              </div>

              {/* Points et options */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Points</Label>
                  <Input
                    type="number"
                    min="1"
                    value={questionForm.points}
                    onChange={(e) => setQuestionForm(prev => ({ ...prev, points: parseInt(e.target.value) || 1 }))}
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
                <div className="flex items-center space-x-2 pt-6">
                  <Switch
                    checked={questionForm.is_required}
                    onCheckedChange={(checked) => setQuestionForm(prev => ({ ...prev, is_required: checked }))}
                  />
                  <Label className="text-gray-300">Question obligatoire</Label>
                </div>
              </div>

              {/* Notation personnalisée */}
              <div>
                <Label className="text-gray-300">Notation</Label>
                <CustomScoring
                  basePoints={questionForm.points}
                  onPointsChange={(points) => setQuestionForm(prev => ({ ...prev, points }))}
                  questionType={questionForm.question_type}
                  scoringRules={questionForm.scoring_rules || []}
                  onScoringRulesChange={(rules) => setQuestionForm(prev => ({ ...prev, scoring_rules: rules }))}
                />
              </div>

              {/* Explication */}
              <div>
                <Label className="text-gray-300">Explication (optionnel)</Label>
                <Textarea
                  value={questionForm.explanation}
                  onChange={(e) => setQuestionForm(prev => ({ ...prev, explanation: e.target.value }))}
                  placeholder="Explication affichée après la réponse..."
                  className="bg-gray-700 border-gray-600 text-white placeholder-gray-400 resize-none"
                  rows={2}
                />
              </div>

              {/* Média */}
              <div>
                <Label className="text-gray-300">Média (optionnel)</Label>
                <MediaUpload
                  currentMediaUrl={questionForm.media_url}
                  currentMediaType={questionForm.media_type}
                  onMediaChange={(url, type) => 
                    setQuestionForm(prev => ({ ...prev, media_url: url, media_type: type }))
                  }
                  onMediaRemove={() => 
                    setQuestionForm(prev => ({ ...prev, media_url: '', media_type: undefined }))
                  }
                />
              </div>

              {/* Réponses */}
              {(questionForm.question_type === 'single_choice' || questionForm.question_type === 'multiple_choice' || questionForm.question_type === 'true_false') && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <Label className="text-gray-300">Réponses</Label>
                    {questionForm.question_type !== 'true_false' && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addAnswer}
                        className="border-green-500 text-green-400 hover:bg-green-500/10"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Ajouter une réponse
                      </Button>
                    )}
                  </div>

                  <div className="space-y-3">
                    {questionForm.question_type === 'true_false' ? (
                      <>
                        <div className="flex items-center space-x-3 p-3 bg-gray-700/50 rounded-lg">
                          <input
                            type="radio"
                            name="true_false"
                            checked={questionForm.answers[0]?.is_correct === true}
                            onChange={() => setQuestionForm(prev => ({
                              ...prev,
                              answers: [
                                { answer_text: 'Vrai', is_correct: true },
                                { answer_text: 'Faux', is_correct: false }
                              ]
                            }))}
                            className="text-green-500"
                          />
                          <span className="text-white">Vrai</span>
                        </div>
                        <div className="flex items-center space-x-3 p-3 bg-gray-700/50 rounded-lg">
                          <input
                            type="radio"
                            name="true_false"
                            checked={questionForm.answers[1]?.is_correct === true}
                            onChange={() => setQuestionForm(prev => ({
                              ...prev,
                              answers: [
                                { answer_text: 'Vrai', is_correct: false },
                                { answer_text: 'Faux', is_correct: true }
                              ]
                            }))}
                            className="text-green-500"
                          />
                          <span className="text-white">Faux</span>
                        </div>
                      </>
                    ) : (
                      questionForm.answers.map((answer, index) => (
                        <div key={index} className="flex items-center space-x-3 p-3 bg-gray-700/50 rounded-lg">
                          <input
                            type={questionForm.question_type === 'multiple_choice' ? 'checkbox' : 'radio'}
                            name="answers"
                            checked={answer.is_correct}
                            onChange={(e) => updateAnswerInForm(index, 'is_correct', e.target.checked)}
                            className="text-green-500"
                          />
                          <Input
                            value={answer.answer_text}
                            onChange={(e) => updateAnswerInForm(index, 'answer_text', e.target.value)}
                            placeholder="Texte de la réponse..."
                            className="bg-gray-600 border-gray-500 text-white flex-1"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeAnswer(index)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            </ScrollArea>

            {/* Prévisualisation en temps réel */}
            <div className="lg:block hidden">
              <QuestionPreview
                questionText={questionForm.question_text}
                questionType={questionForm.question_type}
                points={questionForm.points}
                explanation={questionForm.explanation}
                isRequired={questionForm.is_required}
                answers={questionForm.answers}
              />
            </div>
          </div>
            </TabsContent>

            <TabsContent value="bank">
              <div className="p-4">
                <QuestionBank
                  isInline={true}
                  onQuestionSelected={handleQuestionFromBank}
                />
              </div>
            </TabsContent>

            <TabsContent value="logic">
              <div className="p-4">
                <ConditionalQuestions
                  questionId={editingQuestion?.id}
                  quizId={quiz.id}
                />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsQuestionDialogOpen(false)}
              className="border-gray-600 text-gray-400 hover:bg-gray-700"
            >
              Annuler
            </Button>
            <Button
              onClick={handleSaveQuestion}
              className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {editingQuestion ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Banque de questions */}
      <QuestionBank
        isOpen={isQuestionBankOpen}
        onClose={() => setIsQuestionBankOpen(false)}
        onQuestionSelected={handleQuestionFromBank}
      />
    </div>
  );
};

export { QuestionManager };
